<template>
  <div>
    <h1>Manage Bueraus</h1>
  
    <b-button variant="primary" style="margin-bottom:20px;" v-b-modal.modal-crud>New Bureau</b-button>

    <b-modal id="modal-crud" hide-header hide-footer>
      <h2>{{formTitle}}</h2>
      <hr/>
      <form ref="form" @submit.prevent="save">
          <b-form-group 
            label="Bureau Code" label-for="code-input" 
            invalid-feedback="Bureau Code is required">
            <b-form-input id="code-input" 
              v-model="$v.editedItem.code.$model" 
              :state="$v.editedItem.code.$dirty ? !$v.editedItem.code.$error : null"
              :disabled="editedIndex  !== -1"
              :readonly="editedIndex  !== -1"
              :aria-disabled="editedIndex  !== -1"
              :aria-readonly="editedIndex  !== -1"
              aria-describedby="input-2-live-feedback"
              required>
            </b-form-input> 
            <b-form-invalid-feedback id="input-2-live-feedback">
              This is a required field and must have a length of 4 characters or less.
            </b-form-invalid-feedback>     
            <b-form-text id="code-input-help">Required.</b-form-text> 
          </b-form-group>

          <b-form-group 
            label="Bureau Name" label-for="name-input" 
            invalid-feedback="Bureau Name is required">
            <b-form-input id="name-input" 
              v-model="$v.editedItem.name.$model" 
              :state="$v.editedItem.name.$dirty ? !$v.editedItem.name.$error : null"
              aria-describedby="input-1-live-feedback"
              required>
            </b-form-input> 
            <b-form-invalid-feedback id="input-1-live-feedback">
              This is a required field and must have a length of 75 characters or less.
            </b-form-invalid-feedback>     
            <b-form-text id="name-input-help">Required.</b-form-text> 
          </b-form-group>

          <b-row style="margin-bottom:20px;">
            <b-col>
              <b-form-checkbox v-model="$v.editedItem.active.$model" name="active-switch" data-test="active-switch" switch>
                  <b>Active?</b>
              </b-form-checkbox>
            </b-col>
          </b-row>

          <b-button @click="close" style="margin-right:15px;">Cancel</b-button>
          <b-button type="submit" variant="primary" :disabled="$v.editedItem.$invalid">Save</b-button>
        </form>
    </b-modal>

    <b-row>
      <b-col style="text-align: right; margin-right: 20px;">
        <b-form-checkbox v-model="showInactive" name="showinactive-filter" data-test="showinactive-filter" switch>
            <b>Include inactive bureaus?</b>
        </b-form-checkbox>
      </b-col>
    </b-row>

    <b-table hover small responsive show-empty
      :fields="fields"
      :items="bureaus"
      :busy="isLoading">
      <template #table-caption></template>
      <template v-slot:cell(actions)="row">
        <b-button variant="primary" size="sm" @click="editItem(row.item)" class="mr-1">
          edit
        </b-button>
      </template>
      
      <template v-slot:no-data>
        <v-btn color="primary" @click="initialize">Reset</v-btn>
      </template>

      <template v-slot:empty="scope">
        <p>No bureaus found.</p>
      </template>

      <template v-slot:cell(active)="data">
        {{ data.item.active ? 'Yes' : 'No' }}
      </template>

      <div slot="table-busy" class="text-center text-danger my-2">
          <b-spinner class="align-middle" style="margin-right:5px; margin-top:12px;"></b-spinner>
          <strong>Loading, Please Wait...</strong>
      </div>        

    </b-table>
  </div>
</template>

<script>
import axios from 'axios';
import axiosRetry from 'axios-retry';
import { validationMixin } from 'vuelidate'
import { required, maxLength } from 'vuelidate/lib/validators'
import isBureauUnique from '@/validators/isBureauUnique.js'
import pageTitle from '@/mixins/pageTitle'

  axiosRetry(axios, { retries: 3,
                      retryDelay: axiosRetry.exponentialDelay  });

  export default {
    title: 'Manage Bueraus',
    mixins: [validationMixin, pageTitle],
    data: () => ({
      fields: [
        {key: 'code', sortable: true, label: 'Bureau Code'},
        {key: 'name', sortable: true, label: 'Bureau Name'},
        {key: 'active', sortable: true, label: 'Active?'},
        {key: 'actions', label: 'Actions'}
      ],
      isLoading: false,
      bureaus: [],
      showInactive: false,
      editedIndex: -1,
      editedItem: {
        code: null,
        name: null,
        active: true,
      },
      defaultItem: {
        code: '',
        name: '',
        active: true,
      },
    }),

    validations() {
      if (this.editedIndex === -1) {
        return {
          editedItem: {
            code: {
              required,
              maxLength: maxLength(4),
              isBureauUnique,
            },
            name: {
              required,
              maxLength: maxLength(75),
            },
            active: { },
          }
        }
      } else {
        return {
          editedItem: {
            code: {
              required,
              maxLength: maxLength(4),
            },
            name: {
              required,
              maxLength: maxLength(75),
            },
            active: { },
          }
        }
      }
    },

    computed: {
      formTitle () {
        return this.editedIndex === -1 ? 'New Bureau' : 'Edit Bureau'
      }
    },

    watch: {
      bureaus() {
        this.isLoading = false
      },
      showInactive() {
        this.fetchBureaus()
      }
    },

    created () {
      this.initialize()
    },

    methods: {
      fetchBureaus() {
          this.isLoading = true;
          let fullUrl = `${process.env.VUE_APP_API_BASE_URL}/organization/bureau`
          if (this.showInactive) {
            fullUrl = fullUrl + '/all'
          } 
          axios.get(fullUrl)
            .then(response => {
                this.bureaus = response.data;
                this.isLoading = false;
            })
      },

      initialize () {
        this.fetchBureaus()
      },

      editItem (item) {
        this.editedIndex = this.bureaus.indexOf(item)
        this.editedItem = Object.assign({}, item)
        this.$bvModal.show('modal-crud')
      },

      deleteItem (item) {
        const index = this.bureaus.indexOf(item)
        confirm('Are you sure you want to delete this item?') && this.bureaus.splice(index, 1)
      },

      close () {
        this.$bvModal.hide('modal-crud')
        setTimeout(() => {
          this.editedItem = Object.assign({}, this.defaultItem)
          this.editedIndex = -1
          this.$v.$reset()
          this.fetchBureaus()
        }, 300)
      },

      save () {
        if (this.editedIndex > -1) {
            axios.put(`${process.env.VUE_APP_API_BASE_URL}/organization/bureau`, this.editedItem)
            .then(response => { 
              this.fetchBureaus()
                this.$bvToast.toast('Bureau Updated', {
                  title: 'Status',
                  toaster: 'b-toaster-top-center',
                  solid: true,
                  variant: 'success',
                  appendToast: true
                }) 
            });
          
        } else {
            axios.post(`${process.env.VUE_APP_API_BASE_URL}/organization/bureau`, this.editedItem)
            .then(response => { 
              this.fetchBureaus()
                this.$bvToast.toast('Bureau Saved', {
                  title: 'Status',
                  toaster: 'b-toaster-top-center',
                  solid: true,
                  variant: 'success',
                  appendToast: true
                }) 
            });
          
        }
        this.close()
      },
    }
  }
</script>